import { motion } from "framer-motion";
import { AlertOctagon, PhoneOff, FileWarning, Sparkles, Bot, CheckCircle2 } from "lucide-react";

const PROBLEMS = [
  { icon: AlertOctagon, text: "Claims stuck for 45+ days in paperwork limbo" },
  { icon: PhoneOff, text: "Insurers work for themselves, not for you" },
  { icon: FileWarning, text: "1 in 4 motor claims gets rejected on technicalities" },
];

const SOLUTIONS = [
  { icon: Bot, text: "AI damage estimator & OCR — instant paperwork" },
  { icon: Sparkles, text: "Personal claim manager working 100% for you" },
  { icon: CheckCircle2, text: "5× faster settlements via our network of 12,000+ garages" },
];

export default function ProblemSolution() {
  return (
    <section className="py-24 relative" data-testid="problem-solution">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800 dark:text-blue-400 mb-2">The Problem · The Fix</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
            Insurance is designed to <span className="text-rose-500">confuse you</span>.<br />
            We built the <span className="gradient-text">antidote</span>.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-stretch">
          {/* Problem side */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-rose-200 dark:border-rose-500/20 bg-rose-50/40 dark:bg-rose-500/5 p-8"
            data-testid="problem-card"
          >
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-400 text-[10px] font-bold uppercase tracking-wider mb-4">
              Before us
            </div>
            <h3 className="font-heading font-black text-2xl text-slate-900 dark:text-white mb-6">The claim nightmare</h3>
            <ul className="space-y-4">
              {PROBLEMS.map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 dark:bg-rose-500/15 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 font-medium pt-2">{p.text}</p>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Solution side */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl border border-blue-200 dark:border-blue-700/30 bg-gradient-to-br from-blue-50 to-orange-50 dark:from-blue-700/10 dark:to-orange-500/5 p-8 border-shimmer relative"
            data-testid="solution-card"
          >
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-800 text-white text-[10px] font-bold uppercase tracking-wider mb-4">
              After miclaim
            </div>
            <h3 className="font-heading font-black text-2xl text-slate-900 dark:text-white mb-6">The AI-native claim platform</h3>
            <ul className="space-y-4">
              {SOLUTIONS.map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 shadow-md flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-blue-800 dark:text-blue-400" />
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 font-medium pt-2">{p.text}</p>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
